import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import { FiArrowRight, FiChevronDown, FiMenu, FiX } from 'react-icons/fi';
import {
  FaChartLine,
  FaShieldHalved as FaShieldAlt,
  FaMobileScreenButton as FaMobileAlt,
  FaArrowsRotate as FaSyncAlt,
  FaUsers,
  FaGear as FaCog,
  FaStar,
  FaLinkedinIn,
  FaInstagram,
  FaFacebookF,
  FaXTwitter,
  FaYoutube,
} from 'react-icons/fa6';
import { MdDashboard } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { useRemoteCss } from '../hooks/useRemoteCss';
import axiosInstance from '../api/axiosInstance';
import CookieBanner from '../components/CookieBanner';
import { useCookieConsent } from '../hooks/useCookieConsent';
import AIChatWidget from '../components/AIChatWidget';

import Slider from "react-slick";
//import "slick-carousel/slick/slick.css";
//import "slick-carousel/slick/slick-theme.css";

import './cx.css';

/* ───────── tiny loader ───────── */
const Loader = () => (
  <>
    <div className="loader-wrapper">
      <div className="loader" />
    </div>
    <style>{`
      .loader-wrapper{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#f7f7f7;z-index:9999}
      .loader{width:60px;aspect-ratio:1;display:grid}
      .loader:before,.loader:after{content:"";grid-area:1/1;--c:no-repeat radial-gradient(farthest-side,var(--accent, #b89b6e) 92%,#0000);
        background:var(--c)50%0,var(--c)50%100%,var(--c)100%50%,var(--c)0 50%;background-size:14px 14px;animation:l12 1s infinite}
      .loader:before{margin:5px;filter:hue-rotate(15deg);background-size:9px 9px;animation-timing-function:linear}
      @keyframes l12{100%{transform:rotate(.5turn)}}

      /* Prevent CTA labels from breaking onto 2 lines */
.lux-cta,
.lux-btn-ghost{
  white-space: nowrap;
  flex-shrink: 0;
  width: max-content;
}

    `}</style>
  </>
);
 

const StarRating = ({ value = 5 }) => (
  <div className="stars" aria-label={`${value} star rating`}>
    {[...Array(5)].map((_, i) => (
      <FaStar key={i} className={`star ${i < value ? 'filled' : ''}`} />
    ))}
  </div>
);

const Avatar = ({ src, name, size = 36, className = '' }) => {
  const [err, setErr] = React.useState(false);
  const initials = (name || '')
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const style = { width: size, height: size, fontSize: Math.max(10, size * 0.35) };
  return err || !src ? (
    <div className={`avatar fallback ${className}`} style={style} aria-label={name}>
      {initials || '👤'}
    </div>
  ) : (
    <img
      src={src}
      alt={name}
      className={`avatar ${className}`}
      style={style}
      width={size}
      height={size}
      loading="lazy"
      onError={() => setErr(true)}
    />
  );
};

/* Floating Theme Dock (center of screen, left or right) */
function ThemeDock({ theme, setTheme }) {
  const DOCK_KEY = 'pref-theme-dock';
  const [side, setSide] = useState(() => {
    try {
      return localStorage.getItem(DOCK_KEY) === 'right' ? 'right' : 'left';
    } catch {
      return 'left';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(DOCK_KEY, side);
    } catch {}
  }, [side]);


  return (
    <div
      className={`theme-dock ${side}`}
      role="group"
      aria-label="Theme picker"
      style={{ zIndex: 1200 }}
    >
      {/* Brown (original) */}
      <button
        className={`dock-dot dot-brown ${theme === 'brown' ? 'active' : ''}`}
        onClick={() => setTheme('brown')}
        title="Use Brown (original) theme"
        aria-label="Use Brown (original) theme"
      />
      {/* Green (alt) */}
      <button
        className={`dock-dot dot-green ${theme === 'green' ? 'active' : ''}`}
        onClick={() => setTheme('green')}
        title="Use Green theme"
        aria-label="Use Green theme"
      />
      {/* move dock */}
      <button
        className="dock-side-toggle"
        onClick={() => setSide((s) => (s === 'left' ? 'right' : 'left'))}
        title={`Move dock to ${side === 'left' ? 'right' : 'left'} side`}
        aria-label={`Move dock to ${side === 'left' ? 'right' : 'left'} side`}
      >
        ↔
      </button>
    </div>
  );
}

export default function LandingPage() {
  const { authData } = useAuth();
  const { consent } = useCookieConsent();

  // Theme persistence (DEFAULT = brown/original/light)
  const THEME_KEY = 'pref-theme';
  const initialTheme = (() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved === 'brown' || saved === 'green') return saved;
    } catch {}
    return 'brown';
  })();
  const [theme, setTheme] = useState(initialTheme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme; // <html data-theme="brown|green">
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {}
  }, [theme]);

  // optional remote css
  const cssUrl = `${axiosInstance.defaults.baseURL}/styles/LandingPage`;
  const cssReady = useRemoteCss(cssUrl);

  const navigate = useNavigate();
  const goToRegister = () => navigate('/register');
  const goToLogin = () => navigate('/login');

  /* SEO Meta Tags */
  useEffect(() => {
    document.title = 'Nnetego | Hotel Management System';
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content =
      'All-in-one hotel management platform that simplifies operations, enhances guest experiences, and boosts revenue.';
    document.head.appendChild(metaDescription);

    const metaKeywords = document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content =
      'hotel management system, property management software, PMS, hotel technology, hospitality solutions, hotel operations';
    document.head.appendChild(metaKeywords);

    return () => {
      document.head.removeChild(metaDescription);
      document.head.removeChild(metaKeywords);
    };
  }, []);

  // demo trigger for chat widget
  const [demoTrigger, setDemoTrigger] = useState(null);
  const [showChat, setShowChat] = useState(false);

  // minimum delay so the loader doesn't flicker
  const [minDelayDone, setMinDelayDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinDelayDone(true), 600);
    return () => clearTimeout(t);
  }, []);

  // header state + mobile menu
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMenuOpen(false), [location.pathname]);

  // ===== NAV items (adds How it Works as external PDF) =====
  const NAV = useMemo(
    () => [
      { id: 'home', label: 'Home' },
      { id: 'about', label: 'About Us' },
      { id: 'features', label: 'Features' },
      // { id: 'testimonials', label: 'Testimonials' },
      { label: 'Support', route: '/support' },
      { label: 'How it Works', href: '/docs/how-it-works.pdf' }, // external; no id
    ],
    [],
  );

  // ------ active section for scroll-spy (must be declared BEFORE the effect) ------
  const [active, setActive] = useState('home');

  // Single scroll-spy effect (ignores external PDF item)
  useEffect(() => {
    const sections = NAV
      .filter((n) => n.id && !n.href)
      .map((n) => document.getElementById(n.id))
      .filter(Boolean);
    if (!sections.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: [0.15, 0.4, 0.7] },
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [NAV]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  // Nav click handler: in-page scroll OR open PDF in new tab
  // Nav click handler: in-page scroll OR open PDF in new tab OR internal route
const handleNavClick = (item) => {
  if (item.route) {
    navigate(item.route);
    setMenuOpen(false);
    return;
  }

  if (item.href) {
    window.open(item.href, '_blank', 'noopener,noreferrer');
    setMenuOpen(false);
    return;
  }

  scrollTo(item.id);
};


  if (!cssReady || !minDelayDone) return <Loader />;

  /* ───────── Content data ───────── */
  const features = [
    {
      icon: <FaCog />,
      title: 'Front Desk+',
      description: 'Check-in/out, folios, late checkout, and incident notes.',
    },
    {
      icon: <FaUsers />,
      title: 'Rooms & Rates',
      description: 'Room types, packages, and dynamic pricing—no spreadsheets.',
    },
    { icon: <FaShieldAlt />, title: 'Housekeeping', description: 'Live room status and priorities.' },
    { icon: <FaMobileAlt />, title: 'Mobile Orders', description: 'QR menus, ticket routing, posts to guest folio.' },
    {
      icon: <FaChartLine />,
      title: 'POS & Restaurant',
      description: 'Unified bills; post to room, split checks, track KOT.',
    },
    {
      icon: <FaSyncAlt />,
      title: 'Cloud First',
      description: '24/7 availability—backups and updates handled for you.',
    },
  ];
  const stats = [
    { number: '99.9%', label: 'Uptime SLA' },
    { number: '50+', label: 'Integrations' },
    { number: '5 min', label: 'Average Setup' },
    { number: '24/7', label: 'Support' },
  ];
  const testimonials = [
    {
      text: 'Switched in a weekend. Front Desk+ and QR ordering made ops smoother from day one.',
      name: 'Naledi K.',
      role: 'GM, City Boutique',
      avatar: '/images/avatars/1.jpg',
      rating: 5,
    },
    {
      text: 'Housekeeping priorities and live room status cut our turnaround time by 30%.',
      name: 'Mark D.',
      role: 'Ops Manager, Riverside Inn',
      avatar: '/images/avatars/2.jpg',
      rating: 5,
    },
    {
      text: 'Unified folios and KOT tracking finally aligned our restaurant and rooms revenue.',
      name: 'Aisha M.',
      role: 'F&B Lead, Airport Suites',
      avatar: '/images/avatars/3.jpg',
      rating: 5,
    },
  ];


  const heroSlides = [
  { src: "/images/hero/hms-1.png", alt: "Front Desk dashboard overview" },
  //{ src: "/images/hero/hms-2.png", alt: "Housekeeping live room status board" },
  //{ src: "/images/hero/hms-3.png", alt: "POS and restaurant workflow" },
];

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const heroSliderSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 800,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: !prefersReducedMotion,
  autoplaySpeed: 4500,
  fade: true,
  pauseOnHover: false,
  pauseOnFocus: false,
  swipe: false,
  draggable: false,
};


////

const heroBgSlides = [
  //"/images/Wild.webp",
  "/images/login-background.png",
  "/images/hero/cover-1.webp",
];
 
const heroBgSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 1200,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: !prefersReducedMotion,
  autoplaySpeed: 6000,
  fade: true,
  pauseOnHover: false,
  pauseOnFocus: false,
  swipe: false,
  draggable: false,
};

  
  return (
    <div className="lux-layout">
      {/* Floating theme dock (mid-screen) */}
      <ThemeDock theme={theme} setTheme={setTheme} />

      {/* Topbar */}
      <div className="lux-topbar">Explore our newest modules &amp; integrations.</div>

      {/* ========== HEADER ========== */}
      <header className={`lux-header ${scrolled ? 'is-scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
        <div className="lux-container header-inner">
          <div className="lux-logo" onClick={() => scrollTo('home')} role="button" tabIndex={0}>
            <img src="/images/logo.png" alt="Nnetego" style={{ height: 28 }} />
          </div>

          {/* Nav: buttons only, preserves styling; external PDF just opens in new tab */}
          <nav className={`lux-menu ${menuOpen ? 'open' : ''}`}>
            {NAV.map((item) => (
              <button
                key={item.id || item.label}
                className={`lux-nav-link ${!item.href && active === item.id ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="header-right">
            {authData?.token ? (
              <button className="lux-cta" onClick={() => navigate('/dashboard')}>
                <MdDashboard style={{ marginRight: 8 }} /> Dashboard
              </button>
            ) : (
              <button className="lux-cta" onClick={goToLogin}>
                Get Started
              </button>
            )}
            <button
              className="lux-mobile-toggle"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </header>



      {/* ========== HERO (premium full-screen) ========== */}
      <section id="home" className="lux-hero">
        {/* Soft textured background overlay 
        <div className="lux-hero-bg" aria-hidden="true" />
        */}

        <div className="lux-hero-bg" aria-hidden="true">
          <Slider {...heroBgSettings}>
            {heroBgSlides.map((src) => (
              <div key={src} className="lux-hero-bg-slide">
                <div className="lux-hero-bg-img" style={{ backgroundImage: `url(${src})` }} />
              </div>
            ))}
          </Slider>

          {/* keep your darkening / blend layer */}
          <div className="lux-hero-bg-overlay" aria-hidden="true" />
        </div>


        <div className="lux-container hero-grid">
          {/* LEFT: copy / CTAs */}
          <div className="hero-left">
            <div className="hero-pill-row">
              <span className="brand-tag">Hotel Management Solution</span>
              <span className="hero-pill">Built for modern hospitality</span>
            </div>

            <h1 className="lux-hero-title">
              Attention to detail <span className="thin">in</span> <em>property management</em>
            </h1>

            <p className="lux-hero-sub">
              Run front desk, housekeeping, POS, and mobile orders from a single, calm dashboard—without spreadsheets
              or clashing systems.
            </p>

            <div className="hero-cta-row">
              
              
              <button
  className="lux-cta"
  onClick={() => {
    setShowChat(true);
    setDemoTrigger((n) => (typeof n === "number" ? n + 1 : 1));
  }}
>
  Request Demo <FiArrowRight />
</button>


              <button className="lux-btn-ghost" onClick={goToRegister}>
                Start Free Trial
              </button>
            </div>

            <p className="hero-meta">No long contracts · Go live instantly · Local support</p>
          </div>

          {/* RIGHT: flat preview card with video */}
          <div className="hero-right">
            <div className="hero-card">
              <div className="hero-card-header">
                <span className="hero-card-dot" />
                <span className="hero-card-dot" />
                <span className="hero-card-dot" />
                <span className="hero-card-title">HMS in action</span>
              </div>
              {/*
              <div className="hero-video-frame">
                <video
                  className="hero-video-el"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  poster="/images/Vids/HMSVid-poster.jpg"
                  title="Hotel Management System preview"
                >
                  <source src="/images/Vids/HMSVid.mp4" type="video/mp4" />
                  <source src="/images/Vids/HMSVid.webm" type="video/webm" />
                  Your browser does not support the video tag.
                </video>
              </div>
              */}

              <div className="hero-video-frame" aria-label="Hotel Management System preview slideshow">
                <Slider {...heroSliderSettings}>
                  {heroSlides.map((s, idx) => (
                    <div key={s.src} className="hero-slide">
                      <img
                        className="hero-video-el"
                        src={s.src}
                        alt={s.alt}
                        loading={idx === 0 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    </div>
                  ))}
                </Slider>

                {/* Optional: keep if you had a dark overlay for text readability */}
                <div className="hero-media-overlay" aria-hidden="true" />
              </div>


              <div className="hero-card-footer">
                <div className="hero-foot-stat">
                  <span className="hero-foot-label">Front Desk+</span>
                  <span className="hero-foot-value">Check-in in 3 clicks</span>
                </div>
                <div className="hero-foot-stat">
                  <span className="hero-foot-label">Housekeeping</span>
                  <span className="hero-foot-value">Live room status</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          className="scroll-down-button throb"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          aria-label="Scroll down"
        >
          <FiChevronDown size={22} />
        </button>

        {/* HERO + chrome styling (can be moved to cx.css) */}
        <style>{`
          /* Topbar + header chrome */
          .lux-topbar{
            position:sticky;
            top:0;
            z-index:1300;
            height:32px;
            display:flex;
            align-items:center;
            justify-content:center;
            padding:0 24px;
            font-size:.76rem;
            letter-spacing:.18em;
            text-transform:uppercase;
            color:#f6f2ea;
            background:rgba(10,8,6,.95);
          }
          .lux-header{
            position:sticky;
            top:32px;
            z-index:1290;
            backdrop-filter:blur(22px);
            border-bottom:1px solid rgba(255,255,255,.08);
            transition:background .25s ease, box-shadow .25s ease, transform .2s ease;
          }
          .lux-header.is-scrolled{
            box-shadow: 0 20px 45px rgba(0, 0, 0, 0.19);
          }
          .header-inner{
            display:flex;
            align-items:center;
            justify-content:space-between;
            padding:12px 40px;
          }
          @media (max-width: 768px){
            .header-inner{padding:10px 18px;}
          }
          .lux-logo img{
            display:block;
          }
          .lux-menu{
            display:flex;
            align-items:center;
            gap:6px;
          }
          .lux-menu.open{
            position:fixed;
            inset:64px 16px auto 16px;
            flex-direction:column;
            padding:10px;
            border-radius:16px;
            background:rgba(20,15,10,.98);
            box-shadow:0 18px 50px rgba(0,0,0,.6);
          }
          @media (max-width: 768px){
            .lux-menu{
              display:none;
            }
            .lux-menu.open{
              display:flex;
            }
          }
          .lux-nav-link{
            position:relative;
            padding:8px 18px;
            border-radius:999px;
            border:1px solid transparent;
            background:transparent;
            font-size:.8rem;
            letter-spacing:.18em;
            text-transform:uppercase;
            color:#f7f3ea;
            opacity:.86;
            cursor:pointer;
            transition:background .18s ease, border-color .18s ease, opacity .18s ease, transform .12s ease;
          }
          .lux-nav-link:hover{
            opacity:1;
            background:rgba(255,255,255,.04);
          }
          .lux-nav-link.active{
            opacity:1;
            background:rgba(184,155,110,.14);
            border-color:rgba(184,155,110,.75);
            box-shadow:0 0 0 1px rgba(184,155,110,.45);
          }
          .header-right{
            display:flex;
            align-items:center;
            gap:10px;
          }
          .lux-mobile-toggle{
            display:none;
            border:none;
            background:transparent;
            color:#f7f3ea;
            font-size:1.3rem;
          }
          @media (max-width: 768px){
            .lux-mobile-toggle{
              display:inline-flex;
            }
          }

          /* Global CTAs */
          .lux-cta{
            border:none;
            border-radius:999px;
            padding:10px 26px;
            font-size:.86rem;
            font-weight:600;
            letter-spacing:.16em;
            text-transform:uppercase;
            background:linear-gradient(135deg,#b89b6e,#e6cf9d);
            color:#25170a;
            cursor:pointer;
            display:inline-flex;
            align-items:center;
            gap:8px;
            box-shadow:0 16px 38px rgba(0,0,0,.32);
            transition:transform .12s ease, box-shadow .12s ease, filter .12s ease;
          }
          .lux-cta:hover{
            transform:translateY(-1px);
            box-shadow:0 22px 55px rgba(0,0,0,.38);
            filter:brightness(1.03);
          }
          .lux-btn-ghost{
            border-radius:999px;
            padding:9px 22px;
            border:1px solid rgba(255,255,255,.76);
            background:rgba(15,11,8,.18);
            color:#f8f4ec;
            font-size:.84rem;
            letter-spacing:.12em;
            text-transform:uppercase;
            cursor:pointer;
            display:inline-flex;
            align-items:center;
            gap:6px;
            backdrop-filter:blur(10px);
            transition:background .15s ease, border-color .15s ease, color .15s ease;
          }
          .lux-btn-ghost:hover{
            background:rgba(248,244,236,.12);
            border-color:#f8f4ec;
          }

          /* Theme dock refinements */
          .theme-dock{
            position:fixed;
            top:50%;
            transform:translateY(-50%);
            left:24px;
            padding:6px;
            border-radius:999px;
            background:rgba(15,11,8,.75);
            box-shadow:0 18px 40px rgba(0,0,0,.4);
            backdrop-filter:blur(16px);
            display:flex;
            flex-direction:column;
            gap:6px;
          }
          .theme-dock.right{
            left:auto;
            right:24px;
          }
          .dock-dot{
            width:18px;
            height:18px;
            border-radius:999px;
            border:1px solid rgba(255,255,255,.6);
            cursor:pointer;
            opacity:.8;
            transition:transform .12s ease, box-shadow .12s ease, opacity .12s ease;
          }
          .dot-brown{background:#b89b6e;}
          .dot-green{background:#4f7d5b;}
          .dock-dot.active{
            opacity:1;
            box-shadow:0 0 0 2px rgba(255,255,255,.85);
            transform:scale(1.05);
          }
          .dock-side-toggle{
            width:20px;
            height:20px;
            border-radius:999px;
            border:1px solid rgba(255,255,255,.4);
            background:transparent;
            color:#f8f4ec;
            font-size:.7rem;
            cursor:pointer;
            display:flex;
            align-items:center;
            justify-content:center;
          }
          @media (max-width:768px){
            .theme-dock{
              top:auto;
              bottom:80px;
              transform:none;
            }
          }

          /* HERO layout */
          .lux-hero {
            position: relative;
            min-height: 100vh; /* full screen */
            padding: 140px 0 96px;
            overflow: hidden;
            display:flex;
            align-items:center;
          }
          .lux-hero-bg {
            position: absolute;
            inset: 0;
            background-image: url('/images/login-background.png');
            background-size: cover;
            background-position: center;
            opacity: 0.2;
            mix-blend-mode: multiply;
            pointer-events: none;
          }
          .hero-grid {
            position: relative;
            display: grid;
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
            gap: 56px;
            align-items: center;
            z-index: 1;
          }
          @media (max-width: 960px) {
            .hero-grid {
              grid-template-columns: 1fr;
              gap: 32px;
            }
          }
          .hero-left {
            max-width: 640px;
          }
          .hero-pill-row {
            display: inline-flex;
            flex-wrap: wrap;
            gap: 8px;
            align-items: center;
            margin-bottom: 18px;
          }
          .hero-pill {
            padding: 4px 10px;
            border-radius: 999px;
            border: 1px solid rgba(0, 0, 0, 0.04);
            background: rgba(255, 255, 255, 0.86);
            font-size: 0.78rem;
            text-transform: uppercase;
            letter-spacing: 0.16em;
            color: var(--accent, #b89b6e);
          }
          .lux-hero-title {
            font-size: clamp(2.2rem, 3vw, 2.9rem);
            line-height: 1.08;
            margin-bottom: 14px;
            color: var(--heading-color, #17130f);
          }
          .lux-hero-title .thin {
            font-weight: 300;
          }
          .lux-hero-title em{
            font-style: italic;
          }
          .lux-hero-sub {
            font-size: 1rem;
            line-height: 1.6;
            max-width: 36rem;
            color: var(--copy-color, #534c42);
            margin-bottom: 20px;
          }
          .hero-cta-row {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            align-items: center;
            margin-bottom: 10px;
          }
          .hero-meta {
            font-size: 0.88rem;
            color: #5a5143;
          }
          .hero-right {
            display: flex;
            justify-content: flex-end;
          }
          .hero-card {
            width: 100%;
            max-width: 440px;
            border-radius: 24px;
            background: rgba(255, 255, 255, 0.95);
            border: 1px solid rgba(0, 0, 0, 0.04);
            box-shadow: 0 22px 55px rgba(0, 0, 0, 0.24);
            backdrop-filter: blur(14px);
            overflow: hidden;
          }
          .hero-card-header {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 10px 14px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.04);
          }
          .hero-card-dot {
            width: 8px;
            height: 8px;
            border-radius: 999px;
            background: #e3d3ba;
          }
          .hero-card-title {
            margin-left: auto;
            font-size: 0.8rem;
            text-transform: uppercase;
            letter-spacing: 0.16em;
            color: #8a7b63;
          }
          .hero-video-frame {
            position: relative;
            padding: 0;
            aspect-ratio: 16 / 9;
            background: #000;
            overflow: hidden;
          }
          .hero-video-el {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .hero-card-footer {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            padding: 10px 14px 12px;
            background: #faf7f1;
          }
          .hero-foot-stat {
            flex: 1;
          }
          .hero-foot-label {
            font-size: 0.78rem;
            text-transform: uppercase;
            letter-spacing: 0.14em;
            color: #8b7f6c;
          }
          .hero-foot-value {
            display: block;
            font-size: 0.9rem;
            color: #383127;
          }

          .scroll-down-button{
            position:absolute;
            left:50%;
            bottom:22px;
            transform:translateX(-50%);
            width:40px;
            height:40px;
            border-radius:999px;
            border:1px solid rgba(255,255,255,.65);
            background:rgba(10,7,4,.6);
            color:#fdfbf8;
            display:flex;
            align-items:center;
            justify-content:center;
            cursor:pointer;
            backdrop-filter:blur(14px);
          }
          .scroll-down-button.throb{
            animation:pulseDown 1.6s infinite;
          }
          @keyframes pulseDown{
            0%{transform:translate(-50%,0);opacity:1;}
            50%{transform:translate(-50%,4px);opacity:.7;}
            100%{transform:translate(-50%,0);opacity:1;}
          }
        `}</style>
      </section>

      {/* ========== ABOUT ========== */}
      <section id="about" className="lux-section lux-section--ivory">
        <div className="lux-container">
          <div className="lux-section-head">
            <h2 className="lux-h2">Welcome to Nnetego</h2>
          </div>
          <div className="about-grid">
            <div className="about-copy">
              <p className="lux-copy">
                Experience the blend of contemporary technology and timeless hospitality. Our platform is crafted to
                serve boutique hotels and groups alike—with performance and simplicity at the core.
              </p>
              <p className="lux-copy">
                From Front Desk+ to pure mobile ordering, every module is designed so that <em>Everything Matters</em>.
              </p>
              <button className="lux-cta" onClick={goToRegister}>
                Start Free Trial
              </button>
            </div>
            <div
              className="lux-media lux-media--landscape"
              style={{ backgroundImage: `url(/images/login-background.png)` }}
            />  
              
               
          </div>
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section id="features" className="lux-section lux-section--ivory">
        <div className="lux-container">
          <div className="lux-section-head">
            <h2 className="lux-h2">What Sets Us Apart</h2>
            <p className="lux-copy">Quiet, elegant utility—built for modern hospitality.</p>
          </div>

          <div className="feature-pills">
            {features.map((f, i) => (
              <div key={i} className="feature-pill">
                <div className="pill-icon" aria-hidden="true">
                  {f.icon}
                </div>
                <div className="pill-text">
                  <strong>{f.title}</strong>
                  <small>{f.description}</small>
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          .feature-pills{
            display:grid;
            grid-template-columns:repeat(auto-fit,minmax(260px,1fr));
            gap:14px;
            margin-top:14px;
          }
          .feature-pill{
            display:flex;align-items:flex-start;gap:12px;
            background:var(--card-bg,#fff);border:1px solid var(--card-border,#eee);
            border-radius:16px;padding:16px 18px;
            box-shadow:0 1px 2px rgba(0,0,0,.03);
            transition:transform .2s ease, box-shadow .2s ease;
          }
          .feature-pill:hover{transform:translateY(-2px);box-shadow:0 6px 16px rgba(0,0,0,.06)}
          .feature-pill .pill-icon{
            width:38px;height:38px;border-radius:9999px;
            display:grid;place-items:center;flex:0 0 38px;
            background:var(--pill-bg,#f6f1e8);color:var(--accent,#b89b6e);
          }
          .feature-pill .pill-text strong{font-weight:600;display:block;line-height:1.2;color:var(--heading-color,#111)}
          .feature-pill .pill-text small{display:block;margin-top:4px;color:var(--copy-color,#6b6b6b)}
          @media (max-width: 480px){ .feature-pill{padding:14px} }
        `}</style>
      </section>

      {/* ========== STATS ========== */}
      <section className="stats-section">
        <div className="lux-container stats-wrap">
          {stats.map((s, i) => (
            <div key={i} className="stat-item">
              <div className="stat-number">{s.number}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section
        id="testimonials"
        className="lux-section lux-section--ivory"
        itemScope
        itemType="https://schema.org/Review"
      >
        <div className="lux-container">
          <div className="lux-section-head center">
            <div className="section-eyebrow">Testimonials</div>
            <h2 className="lux-h2">Trusted by Hoteliers</h2>
            <p className="lux-copy">Quiet confidence from real operators.</p>
          </div>

          <div className="tst-grid">
            {testimonials.map((t, i) => (
              <article key={i} className="tst-card" itemScope itemType="https://schema.org/Review">
                <StarRating value={t.rating || 5} />
                <p className="tst-quote" itemProp="reviewBody">
                  {t.text}
                </p>
                <div className="tst-meta" itemProp="author" itemScope itemType="https://schema.org/Person">
                  <Avatar src={t.avatar} name={t.name} size={48} />
                  <div>
                    <div className="tst-name" itemProp="name">
                      {t.name}
                    </div>
                    <div className="tst-role" itemProp="jobTitle">
                      {t.role}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <style>{`
          #testimonials .lux-section-head.center{ text-align:center;margin-bottom:28px }
          #testimonials .section-eyebrow{ letter-spacing:.18em;text-transform:uppercase;color:var(--accent,#b89b6e);font-size:.78rem }
          .tst-grid{ display:grid;gap:16px;grid-template-columns:1fr }
          @media (min-width:900px){ .tst-grid{grid-template-columns:1fr 1fr} }
          .tst-card{
            background:var(--card-bg,#fff);border:1px solid var(--card-border,#eee);
            border-radius:16px;padding:18px 18px 16px; box-shadow:0 1px 2px rgba(0,0,0,.03)
          }
          .tst-quote{ margin:10px 0 12px;font-size:1rem;line-height:1.55;color:var(--ink,#1a1a1a) }
          .tst-meta{ display:flex;align-items:center;gap:10px }
          .tst-name{ font-weight:600;color:var(--heading-color,#111) }
          .tst-role{ color:var(--copy-color,#6b6b6b);font-size:.92rem;margin-top:2px }
          .stars .star{ color:var(--accent,#b89b6e) }
        `}</style>
      </section>

      {/* ========== CTA BANNER ========== */}
      <section className="cta-section">
        <div className="cta-background" style={{ backgroundImage: `url('/images/bck.jpg')` }}>
          <div className="cta-overlay">
            <div className="lux-container">
              <div className="cta-content">
                <h2 className="cta-title">Discover Our Signature Work</h2>
                <p className="cta-text">
                  We aim for the very highest standards—because Everything Matters.
                </p>
                <div className="cta-actions">
                  <button className="lux-cta" onClick={goToRegister}>
                    Start Free Trial
                  </button>
                  <a className="lux-btn-ghost" href="/contact">
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer id="contact" className="lux-footer">
        <div className="lux-container">
          <div className="footer-top">
            <div className="lux-logo lux-logo--footer">
              <img src="/images/logo.png" alt="Nnetego" style={{ height: 28 }} />
            </div>
            <p className="lux-footline" style={{ maxWidth: 480 }}>
              We build modern property-management solutions that help hotels deliver exceptional guest experiences
              while optimizing operations.
            </p>
          </div>

          <div className="footer-columns">
            <div className="foot-col">
              <h4 className="lux-foothead">Head Office</h4>
              <div className="lux-footline">Plot 22211, Nshaiwa Street, Phase 4</div>
              <div className="lux-footline">Gaborone, Botswana</div>
            </div>
            <div className="foot-col">
              <h4 className="lux-foothead">Phone</h4>
              <div className="lux-footline">
                <a href="tel:+26776350228">+267 76 350 228</a>
              </div>
              <div className="lux-footline">Mon–Fri, 8am–5pm CAT</div>
            </div>
            <div className="foot-col">
              <h4 className="lux-foothead">Connect</h4>
              <div className="lux-social">
                <a href="#">
                  <FaLinkedinIn />
                </a>
                <a href="#">
                  <FaInstagram />
                </a>
                <a href="#">
                  <FaFacebookF />
                </a>
                <a href="#">
                  <FaXTwitter />
                </a>
                <a href="#">
                  <FaYoutube />
                </a>
              </div>
            </div>
          </div>

          <hr className="foot-sep" />

          {/* Refined legal/credits row */}
        

            <div className="lux-legal">
              <div className="lux-legal-copy">
                © {new Date().getFullYear()} Nnetego. All rights reserved.{" "}
                <Link to="/legal/privacy">Privacy</Link> • <Link to="/legal/terms">Terms</Link>
              </div>

              <div className="lux-legal-credits">
                Designed by{" "}
                <a href="https://lanesands.com" target="_blank" rel="noopener noreferrer">
                  Lanesands
                </a>
                .
              </div>
            </div>

        </div>

        {consent?.functional && showChat && (
          <AIChatWidget
            startOpen={false}
            openSignal={demoTrigger}
            startQuoteOnOpen
            brandColor="#b89b6e"
            title="Online Assistant"
            subtitle="Let’s get you a quick demo quote"
          />
        )}

      </footer>

      <CookieBanner />
    </div>
  );
}
